import {useEffect, useMemo} from "react";
import {useForm, Controller, useWatch} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row} from "antd";
import Button from "~/components/Button";
import Loading from "~/components/Loading";
import {StatusLabel} from "~/components";
import {
	InputPriceField,
	SelectField,
	GroupRadioField,
	SelectUserAll,
	DateField,
	TextAreaField
} from "~/components/Forms";
import {useUtilities} from "~/hooks";
import {
	slipTypeOptions,
	calcTypeOptions,
	objectOptions,
	defaultCalcBySlipType
} from "../../constants";

const paymentOptions = [
	{ value: 'cod', label: 'Tiền mặt'},
	{ value: 'bank', label: 'Chuyển khoản'},
	{ value: 'momo', label: 'Ví Momo'},
	{ value: 'swipe', label: 'Pos'},
];

function SalarySlipFormAddEdit({item, onHandleSubmit}) {

	const isEdit = Boolean(item?.id);

	const {utilities} = useUtilities();

	const companyOptions = useMemo(() => {
		return Object.entries(utilities.company || {}).map(([key, value]) => ({
			value: key,
			label: <StatusLabel small type={value.color}>{value.title}</StatusLabel>
		}));
	}, [utilities.company]);

	const bankAccountOptions = useMemo(() => {
		return Object.entries(utilities.bankAccounts || {}).map(([key, value]) => ({
			value: key,
			label: value.title,
		}));
	}, [utilities.bankAccounts]);

	const userOptions = (isEdit && typeof item.user?.id != "undefined") ? [{ label : `${item.user.username} - ${item.user.firstname} ${item.user.lastname}`, value : item.user.id }] : [];

	const initialValues = {
		object: 'employee',
		slipType: 'advance',
		calcType: 'minus',
		userId: '',
		companyId: undefined,
		price: 0,
		salaryMonth: new Date(),
		paidDate: new Date(),
		paymentMethod: 'cod',
		bankAccount: '',
		note: '',
	}

	const validationSchema = Yup.object().shape({
		slipType: Yup.string().required('Bạn chưa chọn loại phiếu'),
		calcType: Yup.string().required('Bạn chưa chọn loại tính'),
		price: Yup.number().typeError('Giá trị không hợp lệ').required('Giá trị không được để trống').min(1, 'Giá trị phải lớn hơn 0'),
		salaryMonth: Yup.string().required('Bạn chưa chọn kỳ lương'),
		paidDate: Yup.string().required('Bạn chưa chọn ngày chi thực tế'),
		userId: Yup.mixed().test('require-user', 'Bạn chưa chọn nhân viên', function (value) {
			if (this.parent.object === 'employee') {
				return value !== undefined && value !== null && value !== '';
			}
			return true;
		}),
	})

	const { control, handleSubmit, setValue, formState: { isSubmitting, errors }, reset } = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	const objectType = useWatch({ control, name: 'object' });

	const paymentMethod = useWatch({ control, name: 'paymentMethod' });

	useEffect(() => {
		if(item?.id) {
			initialValues.object = item.object || 'employee';
			initialValues.slipType = item.slipType || 'advance';
			initialValues.calcType = item.calcType || 'minus';
			initialValues.userId = item.userId || '';
			initialValues.companyId = item.companyId ? String(item.companyId) : undefined;
			initialValues.price = item.price;
			initialValues.salaryMonth = item.salaryMonth ? item.salaryMonth*1000 : new Date();
			initialValues.paidDate = item.paidDate ? item.paidDate*1000 : new Date();
			initialValues.paymentMethod = item.paymentMethod || 'cod';
			initialValues.bankAccount = item.bankAccount || '';
			initialValues.note = item.note || '';
		}
		reset(initialValues);
	}, [item]);

	const onSubmit = async (data) => {
		await onHandleSubmit(data, isEdit);
	}

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading/>}
			<p className="heading">Thông tin phiếu</p>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="slipType" render={({ field }) => (
						<SelectField label="Loại phiếu" options={slipTypeOptions} errors={errors} {...field}
							onChange={(value) => {
								field.onChange(value);
								const def = defaultCalcBySlipType[value];
								if (def) setValue('calcType', def);
							}}
						/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="calcType" render={({ field }) => (
						<GroupRadioField label="Loại tính" options={calcTypeOptions} errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="price" render={({field}) => (
						<InputPriceField label="Số tiền" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="salaryMonth" render={({ field }) => (
						<DateField label="Kỳ lương (chi cho tháng)" picker="month" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="paidDate" render={({ field }) => (
						<DateField label="Ngày chi thực tế" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>

			<p className="heading">Đối tượng</p>
			<Controller control={control} name="object" render={({ field }) => (
				<GroupRadioField label="Đối tượng" options={objectOptions} errors={errors} {...field}/>
			)}/>
			{objectType === 'employee' ? (
				<Controller control={control} name="userId" render={({ field }) => (
					<SelectUserAll label="Nhân viên" options={userOptions} errors={errors} {...field} />
				)}/>
			) : (
				<Controller control={control} name="companyId" render={({ field }) => (
					<SelectField label="Công ty" options={companyOptions} errors={errors} {...field}/>
				)}/>
			)}

			<p className="heading">Thanh toán</p>
			<Controller control={control} name="paymentMethod" render={({ field }) => (
				<GroupRadioField label="Phương thức thanh toán" options={paymentOptions} errors={errors} {...field}/>
			)}/>
			{paymentMethod === 'bank' && (
				<Controller control={control} name="bankAccount" render={({ field }) => (
					<SelectField label="Tài khoản ngân hàng" options={bankAccountOptions} errors={errors} {...field}/>
				)}/>
			)}
			<Controller control={control} name="note" render={({ field }) => (
				<TextAreaField label="Ghi chú" errors={errors} {...field} />
			)}/>
			<hr />
			<div className="form-group d-flex justify-content-end modal-bottom">
				<Button primary type="submit">{isEdit ? 'Cập nhật' : 'Thêm'} phiếu lương</Button>
			</div>
		</form>
	)
}

export default SalarySlipFormAddEdit;
